import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { Button } from '@picsart/design-system/components/Button';
import { IconWarningCircleFilled } from '@picsart/design-system/foundation/icons/IconWarningCircleFilled';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { IconRefresh } from '@picsart/design-system/foundation/icons/IconRefresh';
import { useTranslator } from '@picsart/rc/services/localization';
import useStyles from './styles';
import { RATE_LIMIT_COOLDOWN_SECONDS } from '@/consts/generation';

import type { CardErrorProps } from './types';

const CardError = ({
  errorMessage,
  errorType,
  presetTitle,
  onRetry,
  onDismiss,
}: CardErrorProps) => {
  const classes = useStyles();
  const translate = useTranslator();
  const isRateLimited = errorType === 'rate_limited';
  const [cooldown, setCooldown] = useState(
    isRateLimited ? RATE_LIMIT_COOLDOWN_SECONDS : 0
  );
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start countdown timer for rate-limited errors
  useEffect(() => {
    if (!isRateLimited) return;

    intervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRateLimited]);

  // Close popup on scroll
  useEffect(() => {
    if (!showPopup) return;

    const handleScroll = () => setShowPopup(false);
    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, [showPopup]);

  const handleRetryClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (cooldown > 0) return;
      onRetry();
    },
    [onRetry, cooldown]
  );

  const handleDismissClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDismiss();
    },
    [onDismiss]
  );

  const handleMessageClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPopup(true);
  }, []);

  const handleMessageKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      setShowPopup(true);
    }
  }, []);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
      setShowPopup(false);
    }
  }, []);

  const isDisabled = cooldown > 0;
  const retryLabel = isDisabled
    ? translate('Retry in {{seconds}}s', { seconds: cooldown })
    : translate('Retry');

  return (
    <div className={classes.container}>
      <span className={classes.dismissButtonWrapper}>
        <InlineButton
          size="small"
          centerIcon={IconCross}
          className={classes.dismissIconButton}
          isWithoutBorder
          isWithoutBackground
          onClick={handleDismissClick}
          aria-label="Dismiss"
          dataTestId="card-error-dismiss"
        />
      </span>
      <IconWarningCircleFilled
        fill="currentColor"
        className={classes.errorIcon}
        width={32}
        height={32}
      />
      <div className={classes.textBlock}>
        {presetTitle && (
          <span className={classes.presetTitle} title={presetTitle}>
            {presetTitle}
          </span>
        )}
        <span
          className={classes.errorMessage}
          onClick={handleMessageClick}
          onKeyDown={handleMessageKeyDown}
          role="button"
          tabIndex={0}
        >
          {translate(errorMessage)}
        </span>
      </div>
      {showPopup &&
        createPortal(
          <div
            className={classes.popupOverlay}
            onClick={handleOverlayClick}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setShowPopup(false);
            }}
            role="button"
            tabIndex={-1}
          >
            <div ref={popupRef} className={classes.popupContent}>
              <span className={classes.popupCloseWrapper}>
                <InlineButton
                  size="large"
                  centerIcon={IconCross}
                  className={classes.popupCloseIconButton}
                  isWithoutBorder
                  isWithoutBackground
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPopup(false);
                  }}
                  aria-label="Close"
                  dataTestId="card-error-popup-close"
                />
              </span>
              <IconWarningCircleFilled
                fill="currentColor"
                className={classes.errorIcon}
                width={32}
                height={32}
              />
              {presetTitle && (
                <span className={classes.popupPresetTitle}>{presetTitle}</span>
              )}
              <span className={classes.popupText}>
                {translate(errorMessage)}
              </span>
            </div>
          </div>,
          document.body
        )}
      <Button
        dataTestId="card-error-retry"
        skin="error"
        variant="filled"
        size="small"
        startIcon={IconRefresh}
        onClick={handleRetryClick}
        isDisabled={isDisabled}
      >
        {retryLabel}
      </Button>
    </div>
  );
};

export default CardError;
